/* eslint-disable no-unused-vars */
import { Accordion, Button, Group } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import BasicInformationForm from './BasicInformationForm';
import { courseSchema, useCourseStore } from 'store/createCourseStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import DeliveryDetailForm from './DeliveryDetailForm';
import IndicatorStandardForm from './IndicatorStandardForm';
import ActivityForm from './ActivityForm';
import ReviewApprovalForm from './ReviewApprovalForm';
import { HiOutlinePencilAlt, HiOutlineSave } from 'react-icons/hi';
import styles from './styles.module.css';
import BCLDCustomFieldForm from './BCLDCustomFieldForm';
import { fieldToSectionMap, GROUP_TRAINING_TYPE } from '../../../../constant/course';
import { useNavigate } from 'react-router-dom';
import { pathnames } from 'routes/lcms';
import { FormActions } from 'components/forms/FormActions';
import FormProvider from 'providers/FormProviders';
import { courseService } from 'services/lcms/course.service';
import { useQuery } from '@tanstack/react-query';
import { masterAreaService } from 'services/dacum/masterAreaService';

type CourseFormData = z.infer<typeof courseSchema>;

interface CourseFormProps {
  onFormSubmit: (_data: CourseFormData) => void;
  onSaveAsDraft?: (_data: CourseFormData) => void;
  isLoadingEmployeeLecturer?: boolean;
  employeeLecturerData?: any;
}

const CourseForm: React.FC<CourseFormProps> = ({
  onFormSubmit,
  onSaveAsDraft,
  isLoadingEmployeeLecturer = false,
  employeeLecturerData = []
}) => {
  const { formData, mode } = useCourseStore();
  const [currentStep, setCurrentStep] = useState<string[]>(['basicInformation']);
  const navigate = useNavigate();

  const { data: dataCourseCategory } = useQuery({
    queryKey: ['courseServiceGetCourseCategories'],
    queryFn: () => courseService.getCourseCategories({ pageNumber: 1, pageSize: 10000 })
  });

  const { data: dataMasterArea } = useQuery({
    queryKey: ['masterAreaServiceGetList'],
    queryFn: () => masterAreaService.getList({ page: 1, limit: 10000 }),
    staleTime: Infinity
  });

  useEffect(() => {
    if (mode === 'edit') {
      setCurrentStep([
        'basicInformation',
        'bcldCustomField',
        'deliveryDetails',
        'indicatorStandard',
        'activity',
        'reviewApproval'
      ]);
    } else {
      setCurrentStep(['basicInformation']);
    }
  }, [mode]);

  const methods = useForm<CourseFormData>({
    resolver: zodResolver(courseSchema),
    defaultValues: {
      courseName: formData.courseName || '',
      categoryForCompetency: formData.categoryForCompetency || '',
      courseCode: formData.courseCode || '',
      courseCategory: formData.courseCategory || '',
      competencyCategory: formData.competencyCategory || '',
      description: formData.description || '',
      waReminder: formData.waReminder || 'Yes',
      courseType: formData.courseType || '',
      targetAudience: formData.targetAudience || '',
      programCategory: formData.programCategory || '',
      specificJobBand: formData.specificJobBand || '',
      tags: formData.tags || '',
      deliveryType: formData.deliveryType || '',
      learningObjectives: formData.learningObjectives || '',
      learningContent: formData.learningContent || '',
      groupTrainingType: formData.groupTrainingType || '',
      trainingType: formData.trainingType || '',
      levelOfTraining: formData.levelOfTraining || '',
      learningFormat: formData.learningFormat || '',
      fieldOrganizer: formData.fieldOrganizer || '',
      room: formData.room || '',
      trainingLocation: formData.trainingLocation || '',
      modeOfDelivery: formData.modeOfDelivery || '',
      learningHours: formData.learningHours || '',
      weightAttendance: formData.weightAttendance || '',
      weightPreTest: formData.weightPreTest || '',
      weightPostTest: formData.weightPostTest || '',
      weightALP: formData.weightALP || '',
      limIndicator: formData.limIndicator || false,
      liaIndicator: formData.liaIndicator || false,
      passingFormula: formData.passingFormula || '',
      activities: formData.activities || [],
      flagReviewSME: formData.flagReviewSME || false,
      smeInternal: formData.smeInternal || [],
      smeExternal: formData.smeExternal || [],
      picApproval1: formData.picApproval1 || '',
      picApproval2: formData.picApproval2 || '',
      creatorCode: formData.creatorCode || '',
      businessUnits: formData.businessUnits || [],
      targetBinusian: formData.targetBinusian || '',
      jenisTraining: formData.jenisTraining || GROUP_TRAINING_TYPE[0].value,
      bcldLevel: formData.bcldLevel || '1',
      bcldStreamOrFunda: formData.bcldStreamOrFunda || '',
      grpTraining: formData.grpTraining || '1',
      c: formData.c || [],
      status: formData.status || '',
      formulaID: formData.formulaID || ''
    },
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = methods;

  const isAllDataLoaded = dataCourseCategory?.data && dataMasterArea?.data;

  const _courseCategory = dataCourseCategory?.data
    ?.filter((item: { name: string }) => item.name === formData.courseCategory)[0]
    ?.id?.toString();

  const _creatorCode = dataMasterArea?.data
    ?.filter(
      (item: { areaCode: string; areaID: string }) => item.areaCode === formData.creatorCode
    )[0]
    ?.areaID?.toString();

  useEffect(() => {
    if (mode === 'edit' && !isAllDataLoaded) {
      return;
    }
    reset({
      courseName: formData.courseName || '',
      courseCode: formData.courseCode || '',
      categoryForCompetency: formData.categoryForCompetency || '',
      courseCategory: _courseCategory || '',
      competencyCategory: formData.competencyCategory || '',
      description: formData.description || '',
      waReminder: formData.waReminder || 'Yes',
      courseType: formData.courseType || '',
      targetAudience: formData.targetAudience || '',
      programCategory: formData.programCategory || '',
      specificJobBand: formData.specificJobBand || '',
      tags: formData.tags || '',
      deliveryType: formData.deliveryType || '',
      learningObjectives: formData.learningObjectives || '',
      learningContent: formData.learningContent || '',
      groupTrainingType: formData.groupTrainingType || '',
      trainingType: formData.trainingType || '',
      levelOfTraining: formData.levelOfTraining || '',
      learningFormat: formData.learningFormat || '',
      fieldOrganizer: formData.fieldOrganizer || '',
      room: formData.room || '',
      trainingLocation: formData.trainingLocation || '',
      modeOfDelivery: formData.modeOfDelivery || '',
      learningHours: formData.learningHours || '',
      weightAttendance: formData.weightAttendance || '',
      weightPreTest: formData.weightPreTest || '',
      weightPostTest: formData.weightPostTest || '',
      weightALP: formData.weightALP || '',
      limIndicator: formData.limIndicator || false,
      liaIndicator: formData.liaIndicator || false,
      passingFormula: formData.passingFormula || '',
      activities: formData.activities || [],
      flagReviewSME: formData.flagReviewSME || false,
      smeInternal: formData.smeInternal || [],
      smeExternal: formData.smeExternal || [],
      picApproval1: formData.picApproval1 || '',
      picApproval2: formData.picApproval2 || '',
      creatorCode: _creatorCode || '',
      businessUnits: formData.businessUnits || [],
      targetBinusian: formData.targetBinusian || '',
      jenisTraining: formData.jenisTraining || GROUP_TRAINING_TYPE[0].value,
      bcldLevel: formData.bcldLevel || '1',
      bcldStreamOrFunda: formData.bcldStreamOrFunda || '',
      grpTraining: formData.grpTraining || '1',
      c: formData.c || [],
      status: formData.status || '',
      formulaID: formData.formulaID || ''
    });
  }, [formData, reset, isAllDataLoaded, mode]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const errorFields = Object.keys(errors);
      const sectionsWithErrors = new Set<string>();

      errorFields.forEach((field) => {
        const section = fieldToSectionMap[field];
        if (section) {
          sectionsWithErrors.add(section);
        }
      });

      setCurrentStep(Array.from(sectionsWithErrors));
    }
  }, [errors]);

  const activities = watch('activities') || [];
  const limIndicatorValue = watch('limIndicator') || false;
  const passingFormulaValue = watch('passingFormula') || '';

  const onSubmit = (data: CourseFormData) => {
    onFormSubmit(data);
  };

  const onSaveAsDraftClick = () => {
    const currentFormData = watch();
    if (onSaveAsDraft) {
      onSaveAsDraft(currentFormData);
    }
  };

  const handleCancel = () => {
    navigate(pathnames.courses);
  };

  return (
    <section>
      <FormProvider methods={methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Accordion
            className={styles.rootAccordion}
            multiple={true}
            value={currentStep}
            onChange={setCurrentStep}
          >
            <BasicInformationForm control={control} errors={errors} />
            <BCLDCustomFieldForm
              control={control}
              errors={errors}
              passingFormulaValue={passingFormulaValue}
              setValue={setValue}
            />
            <DeliveryDetailForm control={control} errors={errors} />
            <IndicatorStandardForm
              control={control}
              errors={errors}
              limIndicatorValue={limIndicatorValue}
            />
            <ActivityForm activities={activities} setValue={setValue} />
            <ReviewApprovalForm
              control={control}
              errors={errors}
              isLoadingEmployeeLecturer={isLoadingEmployeeLecturer}
              employeeLecturerData={employeeLecturerData}
              setValue={setValue as (_name: string, _value: any) => void}
            />
          </Accordion>

          <FormActions
            mode={mode}
            onCancel={handleCancel}
            showSaveAsDraft={false}
            onSaveAsDraft={onSaveAsDraftClick}
          />
        </form>
      </FormProvider>
    </section>
  );
};

export default CourseForm;
